import { useState } from "react";
import LoadingExperience from "./LoadingExperience";
import usePersistedState from "../lib/usePersistedState";
import { trackEvent } from "../lib/analytics";

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content;
}

/* A bare handle ("acme"), a domain ("acme.com", "acme.myshopify.com") or a
   full URL all pass; the server does the real resolution. This only catches
   obvious non-addresses before a round-trip. */
export function looksLikeStoreAddress(value) {
  const trimmed = (value || "").trim();
  if (!trimmed || /\s/.test(trimmed)) return false;
  return /^(https?:\/\/)?[a-z0-9][a-z0-9.-]*[a-z0-9](\/\S*)?$/i.test(trimmed);
}

/* variant "dark" matches the shopifybundles.com theme (dark card, white
   pill button, no Simple Bundles wording); default is the demo site's
   rounded light look. */
export default function RequestForm({ createPath, variant = "default", buttonLabel = "Build my Simple Bundles" }) {
  const [domain, setDomain] = usePersistedState("request.domain");
  const [email, setEmail] = usePersistedState("request.email");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [statusPath, setStatusPath] = useState(null);
  const [foundDomain, setFoundDomain] = useState(null);

  const submit = async (event) => {
    event.preventDefault();
    if (!looksLikeStoreAddress(domain)) {
      setErrors({ myshopify_domain: ["Enter your .myshopify.com domain or your store's web address (like https://yourstore.com)."] });
      return;
    }
    setSubmitting(true);
    setErrors({});

    try {
      const response = await fetch(createPath, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrfToken() },
        body: JSON.stringify({ domain, email }),
      });
      const data = await response.json();

      if (response.ok) {
        trackEvent("generate_lead", { form: "merchant_demo_request" });
        if (data.status === "ready") {
          window.location.href = data.demo_path;
        } else {
          if (data.resolved) setFoundDomain(data.myshopify_domain);
          setStatusPath(`/d/${data.token}/status`);
        }
      } else {
        setErrors(data.errors || { base: ["Something went wrong. Please try again."] });
        setSubmitting(false);
      }
    } catch {
      setErrors({ base: ["We couldn't reach the server. Please try again."] });
      setSubmitting(false);
    }
  };

  if (statusPath) {
    return <LoadingExperience statusPath={statusPath} dark={variant === "dark"} foundDomain={foundDomain} />;
  }

  const fieldErrors = (key) => (errors[key] || []).join(", ");
  const dark = variant === "dark";
  const inputClasses = `w-full rounded-xl border bg-white px-4 py-3 text-ink outline-none transition placeholder:text-ink/30 ${
    dark
      ? "border-white/10 focus:border-white focus:ring-4 focus:ring-white/20"
      : "border-ink/15 focus:border-sky-deep focus:ring-4 focus:ring-sky-wash"
  }`;
  const buttonClasses = dark ? "pill-light w-full text-base" : "btn-primary w-full text-base";
  const errorClasses = dark ? "text-red-300" : "text-red-600";
  const footnoteClasses = dark ? "text-white/60" : "text-ink-soft";

  return (
    <form onSubmit={submit} className="text-left" noValidate>
      <label className="block mb-4">
        <span className="block text-sm font-semibold mb-1.5">Your Shopify store URL or myshopify domain</span>
        <input
          type="text"
          required
          placeholder="https://yourstore.com or your-store.myshopify.com"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          className={inputClasses}
          autoComplete="off"
          spellCheck="false"
        />
        {fieldErrors("myshopify_domain") && <span className={`mt-1 block text-sm ${errorClasses}`}>{fieldErrors("myshopify_domain")}</span>}
      </label>

      <label className="block mb-5">
        <span className="block text-sm font-semibold mb-1.5">Work email</span>
        <input
          type="email"
          required
          placeholder="you@brand.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={inputClasses}
        />
        {fieldErrors("email") && <span className={`mt-1 block text-sm ${errorClasses}`}>{fieldErrors("email")}</span>}
      </label>

      {fieldErrors("base") && <p className={`mb-4 text-sm ${errorClasses}`}>{fieldErrors("base")}</p>}

      <button type="submit" className={buttonClasses} disabled={submitting}>
        {submitting ? "Starting…" : buttonLabel}
        {!submitting && <span aria-hidden="true">→</span>}
      </button>

      <p className={`mt-4 text-center text-xs ${footnoteClasses}`}>
        Free · No app install · Ready in ~2 minutes
      </p>
    </form>
  );
}
